import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { pushEvent } from '@/lib/analytics'
import {
  generateInvoicePdf,
  getInvoicePdfLatestSignedUrl,
  getInvoicePdfVersionSignedUrl,
  listInvoicePdfVersions,
} from '@/requests/invoices'

export function useInvoicePdfVersionsQuery(invoiceId: string) {
  return useQuery({
    queryKey: ['invoices', invoiceId, 'pdf-versions'],
    queryFn: () => listInvoicePdfVersions(invoiceId),
    enabled: !!invoiceId,
  })
}

export function useGenerateInvoicePdfMutation(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const result = await generateInvoicePdf(invoiceId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['invoices', invoiceId, 'pdf-versions'],
      })
    },
  })
}

export function useInvoicePdfSignedUrlMutation() {
  return useMutation({
    mutationFn: async ({
      invoiceId,
      versionId,
    }: {
      invoiceId: string
      versionId?: string
    }) => {
      const result = versionId
        ? await getInvoicePdfVersionSignedUrl(invoiceId, versionId)
        : await getInvoicePdfLatestSignedUrl(invoiceId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: () => pushEvent('pdf_downloaded', { entity: 'invoice' }),
  })
}
